import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap } from '@cm/core/localization';

const pathStart = 'Candidate|Program History|Other Achievements|';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	feature: HubFeature.CandidateAccessLoginEmcOtherAchievements,
	portal: 'SP',
};
const columnHeadingDescriptionStart = 'Column heading for: ';
const instructions = `<p>Employees in certain roles are required to track their industry certifications.</p>

<p><strong> Add new certification</strong></p>
<ul>
	<li>Link must direct to your achievement (PDF, badge link)</li>
</ul>
<p><strong>Update existing certification</strong></p>
<ul>
	<li>Achievement date must be your renewal date to appear in reporting</li>
</ul>
<p><strong>Request new certification title</strong></p>
<ul>
	<li>
		Submit a
		<a
			href=" https://education.dell.com/content/emc/en-us/internal/home/support-overview/certification-issues.html"
			target="_blank"
			>Proven Support</a
		>
		ticket > CertTracker > I need more help
	</li>
	<li>
		The request must include:
		<ul>
			<li>Name of the Sponsor Organization</li>
			<li>Title of the certification</li>
			<li>
				URL of the sponsor’s official website that goes directly to the requested
				Certification
			</li>
			<li>Scanned copy of your certificate showing current certification</li>
		</ul>
	</li>
</ul>`;

type LocNameStart = 'SPCandidateHistoryOtherAchievements';
export type SPCandidateHistoryOtherAchievementsLocNames =
	| `${LocNameStart}TitleSection`
	| `${LocNameStart}TitleTab`
	| `${LocNameStart}LabelStatus`
	| `${LocNameStart}LabelVendor`
	| `${LocNameStart}LabelName`
	| `${LocNameStart}LabelStatus`
	| `${LocNameStart}LabelCreatedDate`
	| `${LocNameStart}LabelAchievedDate`
	| `${LocNameStart}LabelExpireDate`
	| `${LocNameStart}CertificationId`
	| `${LocNameStart}Reimbursable`
	| `${LocNameStart}RemoveCerts`
	| `${LocNameStart}SuccessNotification`
	| `${LocNameStart}AddButton`
	| `${LocNameStart}TitleInstructions`
	| `${LocNameStart}Instructions`
	| `${LocNameStart}ShowInstructionsButton`
	| `${LocNameStart}HideInstructionsButton`
	| `${LocNameStart}EmployeeInstructions`;

export const spCandidateHistoryOtherAchievements: LocDefs<SPCandidateHistoryOtherAchievementsLocNames> =
	{
		SPCandidateHistoryOtherAchievementsTitleSection: {
			defaultValue: 'Industry Certifications',
			description: 'Page Section Title',
			displayPath: `${pathStart}Page Section Title`,
			displayType: 'Title',
			...sharedProperties,
		},
		SPCandidateHistoryOtherAchievementsTitleTab: {
			defaultValue: 'Industry Certifications',
			description: 'Tab Title',
			displayPath: `${pathStart}Tab Title`,
			displayType: 'Title',
			...sharedProperties,
		},
		SPCandidateHistoryOtherAchievementsLabelStatus: {
			defaultValue: 'Status',
			description: `${columnHeadingDescriptionStart}Status.`,
			displayPath: `${pathStart}Status`,
			displayType: 'Label',
			...sharedProperties,
		},
		SPCandidateHistoryOtherAchievementsLabelVendor: {
			defaultValue: 'Vendor/Sponsor',
			description: `${columnHeadingDescriptionStart}Vendor.`,
			displayPath: `${pathStart}Vendor`,
			displayType: 'Label',
			...sharedProperties,
		},
		SPCandidateHistoryOtherAchievementsLabelName: {
			defaultValue: 'Industry Certification Name',
			description: `${columnHeadingDescriptionStart}Name.`,
			displayPath: `${pathStart}Name`,
			displayType: 'Label',
			...sharedProperties,
		},

		SPCandidateHistoryOtherAchievementsLabelCreatedDate: {
			defaultValue: 'Date Created',
			description: `${columnHeadingDescriptionStart}Date Created.`,
			displayPath: `${pathStart}Date Created`,
			displayType: 'Label',
			...sharedProperties,
		},
		SPCandidateHistoryOtherAchievementsLabelAchievedDate: {
			defaultValue: 'Date Achieved',
			description: `${columnHeadingDescriptionStart}Date Achieved.`,
			displayPath: `${pathStart}Date Achieved`,
			displayType: 'Label',
			...sharedProperties,
		},
		SPCandidateHistoryOtherAchievementsLabelExpireDate: {
			defaultValue: 'Expiration Date',
			description: `${columnHeadingDescriptionStart}Expiration Date.`,
			displayPath: `${pathStart}Expire Date`,
			displayType: 'Label',
			...sharedProperties,
		},
		SPCandidateHistoryOtherAchievementsCertificationId: {
			defaultValue: 'Certification ID',
			description: `${columnHeadingDescriptionStart}Certification ID.`,
			displayPath: `${pathStart}Certification ID`,
			displayType: 'Label',
			...sharedProperties,
		},
		SPCandidateHistoryOtherAchievementsReimbursable: {
			defaultValue: 'Reimbursement Available',
			description: `${columnHeadingDescriptionStart}Reimbursement Available.`,
			displayPath: `${pathStart}Reimbursement Available`,
			displayType: 'Label',
			...sharedProperties,
		},
		SPCandidateHistoryOtherAchievementsRemoveCerts: {
			defaultValue: 'Remove selected certification(s)',
			description: 'Button to add a new certification',
			displayPath: `${pathStart}Remove Certification(s)`,
			displayType: 'Button',
			...sharedProperties,
		},
		SPCandidateHistoryOtherAchievementsAddButton: {
			defaultValue: 'Add Certification',
			description: 'Button to add a new certification',
			displayPath: `${pathStart}Add Certification`,
			displayType: 'Button',
			...sharedProperties,
		},
		SPCandidateHistoryOtherAchievementsSuccessNotification: {
			defaultValue: 'Changes saved.',
			description:
				'Notification shown when a selected set of other achievements get removed from the list.',
			displayPath: `${pathStart}Success Notification`,
			displayType: 'Notification',
			...sharedProperties,
		},
		SPCandidateHistoryOtherAchievementsInstructions: {
			defaultValue: instructions,
			description: "Instructions for the candidate's other achievements page.",
			displayPath: `${pathStart}Instructions`,
			displayType: 'Content',
			shared: false,
			htmlAllowed: true,
			feature: HubFeature.CandidateAccessLoginEmcOtherAchievements,
			portal: 'SP',
		},
		SPCandidateHistoryOtherAchievementsHideInstructionsButton: {
			defaultValue: 'Hide instructions',
			description: 'Button to hide instructions',
			displayPath: `${pathStart}Hide Instructions`,
			displayType: 'Button',
			...sharedProperties,
		},
		SPCandidateHistoryOtherAchievementsShowInstructionsButton: {
			defaultValue: 'Show instructions',
			description: 'Button to show instructions',
			displayPath: `${pathStart}Show Instructions`,
			displayType: 'Button',
			...sharedProperties,
		},
		SPCandidateHistoryOtherAchievementsTitleInstructions: {
			defaultValue: 'Instructions',
			description: 'Page Section Title',
			displayPath: `${pathStart}Instructions Title`,
			displayType: 'Title',
			...sharedProperties,
		},
		SPCandidateHistoryOtherAchievementsEmployeeInstructions: {
			defaultValue:
				'If you are not a Dell Technologies employee, you do not have access to this page. If you are a Dell Technologies employee, please update your demographics with Dell Technologies affiliation and badge number.',
			description: "Instructions for the candidate's other achievements page.",
			displayPath: `${pathStart}Employee Instructions`,
			displayType: 'Content',
			shared: false,
			htmlAllowed: true,
			portal: 'SP',
		},
	};

export type SPCandidateHistoryOtherAchievementsLocType =
	LocMap<SPCandidateHistoryOtherAchievementsLocNames>;
